import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useMenu } from "@/features/menu/store";
import type { MenuPlan, ValidatedMenu } from "@/types/menu";

export const MENU_NEXT_KEY = ["menu", "next"] as const;

/**
 * Loads the validated menu for next week. Priority:
 *   1. Zustand store (nextWeekPlan) — persisted to device
 *   2. GET /api/v1/menu/next — server-validated next-week plan
 *
 * Returns null (not a 404 error) when no next-week menu has been generated.
 */
export function useNextWeekMenuPlan(): {
  plan: MenuPlan | null;
  menuId: string | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const hydrated = useMenu((s) => s._hydrated);
  const nextWeekPlan = useMenu((s) => s.nextWeekPlan);
  const nextWeekMenuId = useMenu((s) => s.nextWeekMenuId);
  const setNextWeekPlan = useMenu((s) => s.setNextWeekPlan);

  const query = useQuery({
    queryKey: MENU_NEXT_KEY,
    queryFn: () => api<ValidatedMenu>("/api/v1/menu/next"),
    enabled: hydrated,
    retry: false,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!query.data) return;
    const serverMenuId = query.data.menu_id ?? null;
    if (
      !nextWeekPlan ||
      (nextWeekMenuId !== null &&
        serverMenuId !== null &&
        serverMenuId !== nextWeekMenuId)
    ) {
      setNextWeekPlan(query.data.plan, serverMenuId);
    }
  }, [query.data, nextWeekPlan, nextWeekMenuId, setNextWeekPlan]);

  return {
    plan: nextWeekPlan ?? query.data?.plan ?? null,
    menuId: nextWeekMenuId,
    isLoading: !nextWeekPlan && query.isLoading,
    // 404 means "no menu yet" — not an error from the UX perspective
    isError:
      query.isError &&
      query.error !== null &&
      !String(query.error).includes("404"),
    refetch: () => query.refetch(),
  };
}
